import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, CheckCircle2, ExternalLink } from 'lucide-react';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Application, ApplicationStatus } from '@core/types';

type Row = {
  jobId: string;
  jobTitle: string;
  total: number;
  shortlisted: number;
  interviewing: number;
  offers: number;
  placed: number;
};

const INTERVIEW_STATUSES: ApplicationStatus[] = [
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
];
const OFFER_STATUSES: ApplicationStatus[] = [
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
];

const DEMO_MOCK_ENABLED = false;

const MOCK_POSITIONS = [
  { jobId: 'mock-1', jobTitle: 'Senior Software Engineer', total: 24, shortlisted: 8, interviewing: 6, offers: 3, placed: 2 },
  { jobId: 'mock-2', jobTitle: 'Product Manager', total: 18, shortlisted: 6, interviewing: 5, offers: 2, placed: 1 },
  { jobId: 'mock-3', jobTitle: 'DevOps Engineer', total: 15, shortlisted: 5, interviewing: 4, offers: 2, placed: 1 },
  { jobId: 'mock-4', jobTitle: 'Data Scientist', total: 12, shortlisted: 4, interviewing: 3, offers: 1, placed: 1 },
  { jobId: 'mock-5', jobTitle: 'UX Designer', total: 10, shortlisted: 4, interviewing: 3, offers: 2, placed: 0 },
];

export function DashboardPositionActivity({ orgId }: { orgId: string | null }) {
  const q = useQuery<Application[]>({
    queryKey: ['dashboardAnalytics', 'applications-all', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      if (!orgId) return [];
      return applicationService.list(orgId, { limit: 500 });
    },
    staleTime: 60_000,
  });

  const rows = useMemo<Row[]>(() => {
    const apps = q.data ?? [];
    const byJob = new Map<string, Row>();
    for (const a of apps) {
      const jobId = a.job_id;
      if (!jobId) continue;
      const jobTitle = a.job?.title || 'Untitled job';
      const cur =
        byJob.get(jobId) ?? {
          jobId,
          jobTitle,
          total: 0,
          shortlisted: 0,
          interviewing: 0,
          offers: 0,
          placed: 0,
        };
      cur.total += 1;
      if (a.status === 'shortlisted') cur.shortlisted += 1;
      if (INTERVIEW_STATUSES.includes(a.status)) cur.interviewing += 1;
      if (OFFER_STATUSES.includes(a.status)) cur.offers += 1;
      if (a.status === 'placed') cur.placed += 1;
      byJob.set(jobId, cur);
    }
    let result = Array.from(byJob.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    
    if (DEMO_MOCK_ENABLED && result.length < 3) {
      result = [...result, ...MOCK_POSITIONS];
    }
    
    return result;
  }, [q.data]);

  const totals = useMemo(() => ({
    total: rows.reduce((s, r) => s + r.total, 0),
    shortlisted: rows.reduce((s, r) => s + r.shortlisted, 0),
    interviewing: rows.reduce((s, r) => s + r.interviewing, 0),
    offers: rows.reduce((s, r) => s + r.offers, 0),
    placed: rows.reduce((s, r) => s + r.placed, 0),
  }), [rows]);

  return (
    <section className="rounded-xl border vscode-border vscode-bg-secondary overflow-hidden">
      <header className="px-5 py-4 border-b vscode-border" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(16, 185, 129, 0.05) 100%)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
              <Briefcase className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold vscode-text-primary">Position activity</h2>
              <p className="text-xs vscode-text-tertiary">Top jobs by application volume</p>
            </div>
          </div>
          <Link
            to="/jobs"
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-400 transition-colors"
          >
            View all
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <div className="p-4">
        {q.isError ? (
          <div className="flex items-center justify-between gap-2 text-xs vscode-text-tertiary py-4">
            <span>Could not load position activity.</span>
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="rounded-lg border vscode-border px-3 py-1.5 text-xs hover:vscode-bg-hover hover:vscode-text-primary"
            >
              Retry
            </button>
          </div>
        ) : q.isLoading ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg vscode-bg-tertiary" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(156, 163, 175, 0.15)' }}>
              <Briefcase className="w-5 h-5 vscode-text-tertiary" />
            </div>
            <p className="text-sm vscode-text-tertiary">No applications yet.</p>
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[560px] table-auto border-collapse text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider vscode-text-tertiary border-b vscode-border">
                  <th className="py-3 pr-3 font-semibold">Position</th>
                  <th className="py-3 pr-3 text-right font-semibold">Total</th>
                  <th className="py-3 pr-3 text-right font-semibold">Shortlisted</th>
                  <th className="py-3 pr-3 text-right font-semibold">Interviewing</th>
                  <th className="py-3 pr-3 text-right font-semibold">Offers</th>
                  <th className="py-3 text-right font-semibold">Placed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                {rows.map((r, idx) => (
                  <tr key={r.jobId} className="hover:vscode-bg-hover transition-colors">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'][idx % 5]} 0%, ${['#1D4ED8', '#059669', '#7C3AED', '#D97706', '#DC2626'][idx % 5]} 100%)` }}
                        >
                          <Briefcase className="w-3.5 h-3.5 text-white" />
                        </div>
                        <Link
                          to={`/jobs/${r.jobId}`}
                          className="font-medium vscode-text-primary hover:text-blue-500 transition-colors truncate"
                        >
                          {r.jobTitle}
                        </Link>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="font-semibold text-blue-500 tabular-nums">{r.total}</span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
                        {r.shortlisted}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
                        {r.interviewing}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8B5CF6' }}>
                        {r.offers}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
                        <CheckCircle2 className="w-3 h-3" />
                        {r.placed}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
